import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";

import { controller as rootController } from "../../../controller";
import { shouldStagePendingReview } from "../../InsuranceRequests/carrierChoice";

// --- Module-level state, shared across hook instances -----------------------
// The SAME patient is covered by TWO mounted instances (TreatmentCard and the
// row's TreatmentPlanListRowInsuranceActions, which the card renders), so a
// per-instance guard still lets two concurrent auto-fetches through — and each
// would create its own policy rows. Keyed by patient so both instances (and
// rapid double-clicks) share one in-flight fetch.
const IN_FLIGHT = new Map(); // patientId -> Promise (POST + optional review)

// Patients the PMS confirmed have NO insurance. The trigger is simply "this
// patient has no policies yet", so without this every insurance action would
// re-run the full Sikka fetch and re-show the toast. Short TTL, mirroring the
// backend's negative cache, so insurance added in the PMS still surfaces.
const NO_PMS_INSURANCE = new Map(); // patientId -> timestamp (ms)
const NO_PMS_INSURANCE_TTL_MS = 10 * 60 * 1000;

// Patients whose policies we refreshed from the PMS recently. Unlike
// ensurePolicies (which fires once, when a patient has NO policies), the refresh
// runs on every request/AI click, so without this a user clicking around a
// patient would re-hit Sikka each time. Short window: long enough to cover one
// interaction, short enough that data entered in the PMS shows up quickly.
const REFRESHED_AT = new Map(); // patientId -> timestamp (ms)
const REFRESH_TTL_MS = 60 * 1000;

// Last per-saved-policy Sikka diff returned for a patient, keyed by patient. The
// board and the composer are separate hook instances; the board's refresh (fired
// by the same click that opens the composer) can win the REFRESH_TTL race, so the
// composer's own refresh short-circuits before it ever sees the previews. Caching
// them here lets that short-circuited path still surface the approve-step hints.
const LAST_PREVIEWS = new Map(); // patientId -> previews map

function refreshedRecently(key) {
  const at = REFRESHED_AT.get(key);
  if (!at) return false;
  if (Date.now() - at > REFRESH_TTL_MS) {
    REFRESHED_AT.delete(key);
    return false;
  }
  return true;
}

function hasFreshNoInsurance(key) {
  const checkedAt = NO_PMS_INSURANCE.get(key);
  if (!checkedAt) return false;
  if (Date.now() - checkedAt > NO_PMS_INSURANCE_TTL_MS) {
    NO_PMS_INSURANCE.delete(key);
    return false;
  }
  return true;
}

/**
 * Auto-fetch a patient's PMS (Sikka) policies when they have none on Smilepass
 * yet, so any insurance action taken on a policy-less patient transparently
 * populates their policies first.
 *
 * Confident carriers are saved silently (no review). An unrecognized carrier
 * comes back for review — the caller renders `<SikkaPolicyReviewModal>` by
 * spreading the returned `reviewProps` (null when no review is pending), and we
 * await that modal before the triggering action continues. Concurrent callers —
 * including a *different* component instance for the same patient — share a
 * single in-flight fetch, then each refreshes its own policy state.
 *
 * @param {number|string} patientId
 * @param {number|string} [officeId]     the patient's own office (advisory — the
 *   backend derives office from the patient; threaded only for consistency).
 * @param {() => Promise<Array>} refreshPolicies  reload + return the caller's
 *   policy list (the caller owns its own policy state).
 * @returns {{ ensurePolicies: (current: Array) => Promise<Array>,
 *             refreshFromPms: () => Promise<Array>,
 *             isFetchingPolicies: boolean, reviewProps: object|null }}
 */
export function useEnsurePolicies(patientId, officeId, refreshPolicies, options) {
  // `quiet`: suppress the toasts, keep the carrier-review modal. For callers that
  // already explain the outcome on screen — the Insurance Requests composer shows
  // "No insurance on file yet ... none came back from your practice management
  // system" right where the policies would be — a toast saying the same thing is
  // pure noise, and on an office with no PMS connection it fires every time.
  // Default false, so the board/row callers are unaffected.
  const quiet = !!(options && options.quiet);
  // `deferReview`: hand the pending review BACK to the caller instead of staging
  // the modal. For a host that has its own review step — the Insurance
  // Verification wizard reviews and approves every policy in step 3 — a centred
  // modal on top of the panel is a second, differently-shaped flow asking the same
  // question. The caller gets { preview_token, policies } and resolves the carrier
  // wherever it makes sense for its own layout. Default false, so every existing
  // caller keeps the modal.
  const deferReview = !!(options && options.deferReview);
  const [isFetchingPolicies, setIsFetchingPolicies] = useState(false);
  const [review, setReview] = useState(null); // { preview } while an unknown carrier awaits review
  // deferReview only: { preview_token, policies } for the caller to resolve itself.
  const [pendingReview, setPendingReview] = useState(null);
  // Per-saved-policy Sikka diff, keyed by policy id — the wizard's approve-step
  // suggestions read from this. Accumulated across fetches so a later refresh only
  // adds to it. Populated on the review AND refresh paths (existing policies too).
  const [pmsPreviews, setPmsPreviews] = useState({});
  const mergePreviews = useCallback(function (incoming) {
    if (incoming && Object.keys(incoming).length) {
      setPmsPreviews(function (prev) { return Object.assign({}, prev, incoming); });
    }
  }, []);
  const resolverRef = useRef(null); // resolves the ensurePolicies() promise when the modal closes

  const resolveReview = useCallback(function resolveReviewInner() {
    setReview(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve();
  }, []);

  // If this instance unmounts while it owns a pending review, resolve it —
  // otherwise the shared in-flight promise never settles and every other caller
  // awaiting this patient hangs forever.
  useEffect(function () {
    return function () {
      const resolve = resolverRef.current;
      resolverRef.current = null;
      if (resolve) resolve();
    };
  }, []);

  // The shared network+review pass, deduped per patient. `allowReview` is false
  // for the refresh path: an unmatched carrier there must NOT hijack the user
  // into the first-time-policy review modal (see refreshFromPms below).
  const runFetch = useCallback(
    function runFetchInner(key, { allowReview }) {
      let run = IN_FLIGHT.get(key);
      if (run) return run;
      run = (async function () {
        // Per-saved-policy Sikka diff (field_states / sikka_values / local_snapshot),
        // keyed by policy id. Surfaced so the wizard's approve step can show the PMS
        // value + any locked local value as inline suggestions. Threaded through the
        // resolved value so EVERY instance awaiting this shared fetch gets it.
        let previews = {};
        try {
          const res = await rootController.autoFetchAndSavePolicies(patientId, officeId);
          if (res && res.status < 250 && res.json) {
            previews = res.json.previews || {};
            if (Object.keys(previews).length) LAST_PREVIEWS.set(key, previews);
            // Part of the PMS lookup failed, so some plan/employer details are
            // missing. The backend deliberately does NOT save in this case —
            // it sends everything for review instead — so tell the user why
            // they're being asked to confirm rather than letting it look like
            // a normal unknown-carrier prompt.
            const warnings = res.json.warnings || [];
            if (warnings.length && allowReview && !quiet) {
              message.warning(
                "Some details couldn't be read from the PMS, so nothing was saved automatically. Please review before saving.",
              );
            }
            if (res.json.needs_review && res.json.preview_token) {
              NO_PMS_INSURANCE.delete(key);
              if (deferReview) {
                // The caller owns the review. Return rather than block — there is
                // no modal to wait for.
                return {
                  preview_token: res.json.preview_token,
                  policies: res.json.policies || [],
                  previews,
                };
              }
              if (allowReview) {
                await new Promise(function (resolve) {
                  resolverRef.current = resolve;
                  setReview({
                    preview: {
                      preview_token: res.json.preview_token,
                      policies: res.json.policies || [],
                    },
                  });
                });
              }
            } else if (res.json.no_insurance) {
              NO_PMS_INSURANCE.set(key, Date.now());
              if (allowReview && !quiet) {
                // not_in_pms: added by hand, not linked to a PMS record yet — the
                // server didn't ask the PMS, so "none found there" would mislead.
                message.info(
                  res.json.not_in_pms
                    ? "This patient isn't in your PMS yet. Add a policy manually."
                    : "No insurance found in the PMS for this patient. Add a policy manually.",
                );
              }
            } else {
              NO_PMS_INSURANCE.delete(key);
            }
          } else if (allowReview && !quiet) {
            // Soft, not an error: the caller carries on regardless and the user
            // can still enter the policy by hand, so this must not read as "your
            // request failed" — which is exactly how it looked for offices whose
            // Sikka account is configured but not active. Deliberately NOT the
            // raw backend string, which is operator-facing ("Office D16096 not
            // found in authorized practices for the PMS account").
            message.warning(
              "Couldn't reach the practice management system — you can still enter the policy details manually.",
            );
          }
          return { previews };
        } catch (e) {
          console.error(e);
          if (allowReview && !quiet) {
            message.warning(
              "Couldn't reach the practice management system — you can still enter the policy details manually.",
            );
          }
        }
        return { previews };
      })();
      IN_FLIGHT.set(key, run);
      run.then(
        function () { if (IN_FLIGHT.get(key) === run) IN_FLIGHT.delete(key); },
        function () { if (IN_FLIGHT.get(key) === run) IN_FLIGHT.delete(key); },
      );
      return run;
    },
    [patientId, officeId, quiet, deferReview],
  );

  const ensurePolicies = useCallback(
    async function ensurePoliciesInner(currentPolicies) {
      if (Array.isArray(currentPolicies) && currentPolicies.length > 0) {
        return currentPolicies;
      }
      if (!patientId) return [];
      const key = String(patientId);

      // Already established the PMS has nothing for this patient — don't re-fetch
      // or re-toast on every subsequent insurance action.
      if (hasFreshNoInsurance(key)) {
        return Array.isArray(currentPolicies) ? currentPolicies : [];
      }

      // One network+review pass per patient, shared by all callers.
      const run = runFetch(key, { allowReview: true });

      // Each caller shows its own spinner and refreshes its OWN policy state —
      // the shared promise only covers the fetch + review.
      setIsFetchingPolicies(true);
      try {
        const outcome = await run;
        // deferReview: the shared promise resolves WITH the pending review, so
        // every instance awaiting this patient sees the same one.
        if (outcome && outcome.preview_token) setPendingReview(outcome);
        if (outcome) mergePreviews(outcome.previews);
      } catch (e) {
        // The shared pass already reports its own failures; never reject — one
        // call site fires this without awaiting.
        console.error(e);
      } finally {
        setIsFetchingPolicies(false);
      }
      return refreshPolicies ? refreshPolicies() : [];
    },
    [patientId, officeId, refreshPolicies, runFetch, mergePreviews],
  );

  /**
   * Top up an EXISTING policy set from the PMS.
   *
   * `ensurePolicies` deliberately short-circuits when the patient already has
   * policies, so a patient holding one policy with a blank member ID never
   * re-hits the PMS. The pre-request completeness check needs the opposite: pull
   * whatever the PMS now has so the user isn't asked to type in data we could
   * have fetched. Safe to run over existing rows because auto-fetch-and-save
   * writes only fields absent from `manually_edited_fields`, so human edits win.
   *
   * Fire-and-forget by design — callers start it on click and let the policy
   * selection step cover the latency. Never shows the carrier-review modal: that
   * flow is for first-time policy creation, and hijacking a request click with it
   * would be jarring. An unmatched carrier stays fixable from Policy Information.
   */
  const refreshFromPms = useCallback(
    async function refreshFromPmsInner() {
      if (!patientId) return [];
      const key = String(patientId);
      if (hasFreshNoInsurance(key) || refreshedRecently(key)) {
        // A recent fetch (often the board's, from the same click) already has the
        // diff — surface it so the composer's suggestions don't go missing.
        mergePreviews(LAST_PREVIEWS.get(key));
        return refreshPolicies ? refreshPolicies() : [];
      }
      REFRESHED_AT.set(key, Date.now());
      try {
        const outcome = await runFetch(key, { allowReview: false });
        if (outcome) mergePreviews(outcome.previews);
        // A caller that owns the review (the request wizard) gets the PMS policies
        // we couldn't match even when the patient already has a policy — this
        // used to drop an unmatched secondary on the floor without a word.
        if (shouldStagePendingReview(deferReview, outcome)) setPendingReview(outcome);
      } catch (e) {
        console.error(e);
      }
      return refreshPolicies ? refreshPolicies() : [];
    },
    [patientId, refreshPolicies, runFetch, mergePreviews, deferReview],
  );

  const reviewProps = review
    ? {
        open: true,
        preview: review.preview,
        patientID: patientId,
        onClose: resolveReview,
        onSaved: function () {
          if (refreshPolicies) refreshPolicies();
          resolveReview();
        },
      }
    : null;

  const clearPendingReview = useCallback(function () { setPendingReview(null); }, []);

  /**
   * Mint a FRESH preview token for the same patient.
   *
   * The backend caches a preview for 15 minutes and a wizard session can outlive
   * that, at which point saving answers `preview_expired`. Deliberately bypasses
   * every cache and dedupe above — this is called precisely because the cached
   * answer is gone, so reusing an in-flight or recent one would return the same
   * dead token.
   */
  const refetchPreview = useCallback(
    async function refetchPreviewInner() {
      if (!patientId) return null;
      try {
        const res = await rootController.autoFetchAndSavePolicies(patientId, officeId);
        if (res && res.status < 250 && res.json && res.json.preview_token) {
          const fresh = {
            preview_token: res.json.preview_token,
            policies: res.json.policies || [],
          };
          setPendingReview(fresh);
          return fresh;
        }
      } catch (e) {
        console.error(e);
      }
      return null;
    },
    [patientId, officeId],
  );

  return {
    ensurePolicies,
    refreshFromPms,
    isFetchingPolicies,
    reviewProps,
    // deferReview callers only; null for everyone else.
    pendingReview,
    clearPendingReview,
    refetchPreview,
    // Per-saved-policy Sikka diff for the approve-step suggestions, keyed by policy id.
    pmsPreviews,
  };
}
